'use client';

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Stack,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  RemoveCircle as RemoveCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';
import { getStockSentiment, getPortfolios } from '../../lib/api';
import { formatDataTimestamp, formatCacheTimestamp, formatArticleDate } from '../../lib/timezone';

interface NewsArticle {
  title: string;
  description: string;
  source: string;
  publishedAt: string;
  url: string;
}

interface MoneyFlowIndicators {
  symbol: string;
  obv: number;
  cmf: number;
  trend: 'ACCUMULATION' | 'DISTRIBUTION'; // CMF >= 0 = ACCUMULATION, CMF < 0 = DISTRIBUTION
  timestamp: string;
}

interface StockSentiment {
  stocks: StockAnalysis[];
  timestamp: string;
  cached?: boolean;
  cacheStatus?: 'HIT' | 'MISS' | 'STALE_REFRESH'; // v3.4.5: DO-only cache (v3.3.0+)
  cachedAt?: string;
  missDetails?: 'L1_AND_L2' | 'L1_ONLY';
}

interface StockAnalysis {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
  gptContext: string;
  distilbertSentiment: number; // -1 to +1
  sentimentLabel: 'BEARISH' | 'NEUTRAL' | 'BULLISH';
  agreement: 'AGREE' | 'PARTIAL' | 'DISAGREE';
  signal: 'STRONG_BUY' | 'OPPORTUNITY' | 'BULL_TRAP' | 'STRONG_SELL';
  confidence: number; // 0-100%
  moneyFlow: MoneyFlowIndicators | null;
  newsCount: number;
  articles: NewsArticle[];
}

interface Portfolio {
  id: string;
  name: string;
  description: string;
  symbols: string[];
  createdAt: string;
  updatedAt: string;
}

export function PortfolioSentimentWidget() {
  const [sentiment, setSentiment] = useState<StockSentiment | null>(null);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Normalize API response to match widget expectations
  const normalizeStockSentiment = (data: any): StockSentiment => {
    const stocks = Array.isArray(data?.stocks) ? data.stocks : [];
    return {
      stocks: stocks.map((stock: any) => ({
        ...stock,
        signal: stock.signal ?? stock.finalSignal ?? 'STRONG_BUY',
        change: stock.change ?? stock.priceChange ?? 0,
        changePercent: stock.changePercent ?? stock.priceChangePercent ?? 0,
        newsCount: stock.newsCount ?? stock.articles?.length ?? 0,
        articles: stock.articles ?? [],
        moneyFlow: stock.moneyFlow ?? null,
      })),
      timestamp: data?.timestamp ?? new Date().toISOString(),
      cached: data?.cached ?? false,
      cacheStatus: data?.cacheStatus,
      cachedAt: data?.cachedAt,
      missDetails: data?.missDetails,
    };
  };

  // Fetch portfolios on mount
  useEffect(() => {
    async function fetchPortfolios() {
      try {
        const data = await getPortfolios();
        const portfolioList = (Array.isArray(data) ? data : []) as Portfolio[];
        setPortfolios(portfolioList);

        // Select active portfolio or first portfolio
        const activePortfolio =
          portfolioList.find((p: any) => p.isActive) ||
          portfolioList.find((p: any) => p.id?.includes?.('active')) ||
          null;

        if (activePortfolio) {
          setSelectedPortfolio(activePortfolio.id);
        } else if (portfolioList.length > 0) {
          setSelectedPortfolio(portfolioList[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch portfolios:', err);
      }
    }

    fetchPortfolios();
  }, []);

  // Fetch sentiment when portfolio changes
  useEffect(() => {
    if (!selectedPortfolio) return;

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        const data = await getStockSentiment(selectedPortfolio);
        const normalized = normalizeStockSentiment(data);
        setSentiment(normalized);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch stock sentiment');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    const interval = setInterval(fetchData, 15 * 60 * 1000); // 15 min
    return () => clearInterval(interval);
  }, [selectedPortfolio]);

  const handlePortfolioChange = (event: SelectChangeEvent) => {
    setSelectedPortfolio(event.target.value);
  };

  const getSignalColor = (signal: string) => {
    switch (signal) {
      case 'STRONG_BUY':
        return 'success';
      case 'OPPORTUNITY':
        return 'info';
      case 'STRONG_SELL':
        return 'error';
      case 'BULL_TRAP':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getSignalIcon = (signal: string) => {
    switch (signal) {
      case 'STRONG_BUY':
        return <CheckCircleIcon fontSize="small" />;
      case 'OPPORTUNITY':
        return <StarIcon fontSize="small" />;
      case 'STRONG_SELL':
        return <CancelIcon fontSize="small" />;
      case 'BULL_TRAP':
        return <ErrorIcon fontSize="small" />;
      default:
        return <RemoveCircleIcon fontSize="small" />;
    }
  };

  const getAgreementColor = (agreement: string) => {
    switch (agreement) {
      case 'AGREE':
        return 'success';
      case 'PARTIAL':
        return 'warning';
      default:
        return 'error';
    }
  };

  const getSentimentColor = (label: string) => {
    switch (label) {
      case 'BULLISH':
        return 'success';
      case 'BEARISH':
        return 'error';
      default:
        return 'warning';
    }
  };

  const getMoneyFlowColor = (trend: string) => {
    switch (trend) {
      case 'ACCUMULATION':
        return 'success';
      case 'DISTRIBUTION':
        return 'error';
      default:
        return 'default';
    }
  };

  const getQuadrant = (stock: StockAnalysis): { name: string; color: string; description: string } | null => {
    if (!stock.moneyFlow) return null;

    const newsBullish = stock.distilbertSentiment > 0.2;
    const newsBearish = stock.distilbertSentiment < -0.2;
    const moneyBullish = stock.moneyFlow.trend === 'ACCUMULATION';

    // QUADRANT I: STRONG_BUY
    if (newsBullish && moneyBullish) {
      return {
        name: 'STRONG_BUY',
        color: '#4caf50',
        description: 'News bullish + Money accumulation'
      };
    }

    // QUADRANT II: OPPORTUNITY
    if (newsBearish && moneyBullish) {
      return {
        name: 'OPPORTUNITY',
        color: '#2196f3',
        description: 'News bearish + Money accumulation (contrarian)'
      };
    }

    // QUADRANT III: STRONG_SELL
    if (newsBearish && !moneyBullish) {
      return {
        name: 'STRONG_SELL',
        color: '#f44336',
        description: 'News bearish + Money distribution'
      };
    }

    // QUADRANT IV: BULL_TRAP
    if (newsBullish && !moneyBullish) {
      return {
        name: 'BULL_TRAP',
        color: '#ff9800',
        description: 'News bullish + Money distribution (warning!)'
      };
    }

    // Neutral news: classify by money flow only
    return moneyBullish
      ? { name: 'OPPORTUNITY', color: '#2196f3', description: 'Neutral news + Money accumulation' }
      : { name: 'BULL_TRAP', color: '#ff9800', description: 'Neutral news + Money distribution' };
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error || !sentiment) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" icon={<ErrorIcon />}>
            {error || 'Failed to load portfolio sentiment'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" component="div">
            Portfolio Stock Sentiment
          </Typography>

          {/* Portfolio Selector */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select value={selectedPortfolio} onChange={handlePortfolioChange} displayEmpty>
              {portfolios.map((portfolio) => (
                <MenuItem key={portfolio.id} value={portfolio.id}>
                  {portfolio.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Stock Analysis - Responsive Layout */}
        {/* Desktop: Table view */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, overflowX: 'auto' }}>
          <TableContainer>
            <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Symbol</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>AI Analysis</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Sentiment
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Money Flow
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Quadrant
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Signal
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Confidence
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sentiment.stocks.map((stock) => {
                const isHighConfidence = stock.confidence >= 70;
                const isExpanded = expandedRow === stock.symbol;
                return (
                <>
                <TableRow
                  key={stock.symbol}
                  sx={{
                    backgroundColor: isHighConfidence
                      ? 'rgba(255, 193, 7, 0.08)' // Amber highlight for high confidence
                      : 'transparent',
                    borderLeft: isHighConfidence
                      ? '4px solid #ffc107' // Amber left border
                      : 'none',
                    '&:hover': {
                      backgroundColor: isHighConfidence
                        ? 'rgba(255, 193, 7, 0.12)'
                        : 'rgba(255, 255, 255, 0.03)',
                      cursor: 'pointer',
                    },
                  }}
                  onClick={() => setExpandedRow(isExpanded ? null : stock.symbol)}
                >
                  {/* Symbol */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isHighConfidence && (
                        <StarIcon sx={{ color: '#ffc107', fontSize: 20 }} />
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {stock.symbol}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {stock.newsCount} news
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Price */}
                  <TableCell>
                    <Typography variant="body2">${stock.price.toFixed(2)}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {stock.change >= 0 ? (
                        <TrendingUpIcon fontSize="small" color="success" />
                      ) : (
                        <TrendingDownIcon fontSize="small" color="error" />
                      )}
                      <Typography
                        variant="caption"
                        color={stock.change >= 0 ? 'success.main' : 'error.main'}
                      >
                        {stock.changePercent.toFixed(2)}%
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* AI Context */}
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="caption" sx={{ fontStyle: 'italic', lineHeight: 1.4 }}>
                      {stock.gptContext}
                    </Typography>
                  </TableCell>

                  {/* Sentiment */}
                  <TableCell align="center">
                    <Chip
                      label={stock.sentimentLabel}
                      color={getSentimentColor(stock.sentimentLabel) as any}
                      size="small"
                    />
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {stock.distilbertSentiment.toFixed(2)}
                    </Typography>
                  </TableCell>

                  {/* Money Flow */}
                  <TableCell align="center">
                    {stock.moneyFlow ? (
                      <>
                        <Chip
                          label={stock.moneyFlow.trend}
                          color={getMoneyFlowColor(stock.moneyFlow.trend) as any}
                          size="small"
                        />
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          CMF: {stock.moneyFlow.cmf.toFixed(3)}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>

                  {/* Quadrant */}
                  <TableCell align="center">
                    {(() => {
                      const quadrant = getQuadrant(stock);
                      return quadrant ? (
                        <Chip
                          label={quadrant.name}
                          size="small"
                          sx={{
                            backgroundColor: quadrant.color,
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Neutral
                        </Typography>
                      );
                    })()}
                  </TableCell>

                  {/* Signal */}
                  <TableCell align="center">
                    <Chip
                      label={stock.signal}
                      color={getSignalColor(stock.signal) as any}
                      icon={getSignalIcon(stock.signal)}
                      sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                    />
                  </TableCell>

                  {/* Confidence */}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={stock.confidence}
                        sx={{
                          width: 60,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: getSignalColor(stock.signal) + '.main',
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        fontWeight={isHighConfidence ? 700 : 600}
                        sx={{
                          color: isHighConfidence ? '#ffc107' : 'inherit'
                        }}
                      >
                        {stock.confidence}%
                      </Typography>
                      {isHighConfidence && (
                        <Typography
                          variant="caption"
                          sx={{
                            backgroundColor: '#ffc107',
                            color: '#000',
                            px: 0.5,
                            borderRadius: 0.5,
                            fontWeight: 700,
                            fontSize: '0.65rem',
                          }}
                        >
                          HIGH
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Expandable Row for News Articles */}
                {isExpanded && stock.articles && stock.articles.length > 0 && (
                  <TableRow key={`${stock.symbol}-articles`}>
                    <TableCell colSpan={8} sx={{ py: 2, backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <ArticleIcon fontSize="small" />
                        <Typography variant="subtitle2" fontWeight={700}>
                          News Articles ({stock.newsCount})
                        </Typography>
                      </Box>
                      <Stack spacing={1.5}>
                        {stock.articles.map((article, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              p: 1.5,
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: 1,
                              borderLeft: '3px solid',
                              borderColor: 'primary.main',
                            }}
                          >
                            <Link
                              href={article.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              color="inherit"
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {article.title}
                              </Typography>
                            </Link>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}
                            >
                              {article.description}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Chip label={article.source} size="small" variant="outlined" />
                              <Typography variant="caption" color="text.secondary">
                                {formatArticleDate(article.publishedAt)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
                </>
              );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>

        {/* Mobile: Card view */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {sentiment.stocks.map((stock) => {
            const quadrant = getQuadrant(stock);
            const isHighConfidence = stock.confidence >= 70;
            
            return (
              <Card 
                key={stock.symbol}
                sx={{ 
                  mb: 2,
                  borderLeft: isHighConfidence ? '4px solid #ffc107' : 'none',
                  backgroundColor: isHighConfidence ? 'rgba(255, 193, 7, 0.08)' : 'transparent'
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Header: Symbol + Price */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {stock.symbol}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stock.name}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        ${stock.price.toFixed(2)}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ color: stock.changePercent >= 0 ? 'success.main' : 'error.main' }}
                      >
                        {stock.changePercent >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Signal + Confidence */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <Chip
                      icon={getSignalIcon(stock.signal)}
                      label={stock.signal}
                      color={getSignalColor(stock.signal)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={`${stock.confidence}%`}
                      size="small"
                      color={isHighConfidence ? 'warning' : 'default'}
                    />
                  </Box>

                  {/* Sentiment + Money Flow */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Sentiment
                      </Typography>
                      <Chip
                        label={stock.sentimentLabel}
                        color={getSentimentColor(stock.sentimentLabel)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    {stock.moneyFlow && (
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Money Flow
                        </Typography>
                        <Chip
                          label={stock.moneyFlow.trend}
                          color={getMoneyFlowColor(stock.moneyFlow.trend)}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Quadrant */}
                  {quadrant && (
                    <Box sx={{ mt: 1.5, p: 1, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Quadrant
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: quadrant.color, borderRadius: '50%' }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {quadrant.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {quadrant.description}
                      </Typography>
                    </Box>
                  )}

                  {/* AI Analysis (expandable) */}
                  <Box sx={{ mt: 1.5 }}>
                    <Typography 
                      variant="caption" 
                      color="primary" 
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => setExpandedRow(expandedRow === stock.symbol ? null : stock.symbol)}
                    >
                      {expandedRow === stock.symbol ? 'Hide' : 'Show'} AI Analysis
                    </Typography>
                    {expandedRow === stock.symbol && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {stock.gptContext}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
          Powered by Dual AI (GPT-OSS + DistilBERT) • On-demand analysis • Sentiment 24h • Money flow 12h
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Data sourced: {formatDataTimestamp(sentiment.timestamp)}
          </Typography>
          {sentiment.cachedAt && (
            <Typography
              variant="caption"
              sx={{
                color: sentiment.cacheStatus === 'HIT' ? '#4caf50' : sentiment.cacheStatus === 'STALE_REFRESH' ? '#ff9800' : '#9e9e9e',
                fontWeight: 600
              }}
            >
              Cache: {sentiment.cacheStatus}
              {' '}({formatCacheTimestamp(sentiment.cachedAt)})
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
